import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Percent as PercentIcon,
  Flag as FlagIcon,
  PendingActions as PendingActionsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ReferenceLine, Cell, LabelList,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';

// ─── Tipos ───

interface RegulacaoTotais {
  total_regulado: number;
  tempo_medio_dias: number;
  na_meta_pct: number;
  pendentes: number;
  meta_dias: number;
}

interface PorServico {
  servico_tipo_id: number | null;
  servico_nome: string;
  regulados: number;
  tempo_medio_dias: number;
  dentro_meta: number;
  pendentes: number;
  na_meta: boolean;
}

interface TempoRegulacao {
  totais: RegulacaoTotais;
  por_servico: PorServico[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

interface PorProfissional {
  regulador: string;
  regulados: number;
  tempo_medio_dias: number;
  dentro_meta: number;
  na_meta: boolean;
}

interface ServicoDetalhe {
  servico_tipo_id: number | null;
  servico_nome: string | null;
  totais: RegulacaoTotais;
  por_profissional: PorProfissional[];
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

const REFRESH_MS = 60_000;

const hojeISO = (): string => new Date().toISOString().slice(0, 10);
const inicioAnoISO = (): string => `${new Date().getFullYear()}-01-01`;

/** Tempo em dias -> texto curto pt-BR ("12,4 dias" / "1 dia"). */
const formatarDias = (dias: number | null | undefined): string => {
  if (dias == null || dias <= 0) return '—';
  const txt = Number(dias).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return `${txt} ${Number(dias) === 1 ? 'dia' : 'dias'}`;
};

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

/** Chip "Na meta" / "Fora da meta" — verde quando o tempo medio <= meta. */
const ChipMeta: React.FC<{ naMeta: boolean; semDados?: boolean }> = ({ naMeta, semDados }) => {
  if (semDados) return <Chip size="small" variant="outlined" label="Sem regulações" />;
  return naMeta
    ? <Chip size="small" color="success" icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} label="Na meta" />
    : <Chip size="small" color="error" icon={<CancelIcon sx={{ fontSize: 16 }} />} label="Fora da meta" />;
};

// ─── Pagina ───

const SaudeTempoRegulacao: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<TempoRegulacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtro Periodo — default ano corrente (01/01 -> hoje)
  const [inicio, setInicio] = useState<string>(inicioAnoISO());
  const [fim, setFim] = useState<string>(hojeISO());

  // Detalhe do servico (POC 16.20) — modal com tempo medio + meta + por profissional
  const [servicoRef, setServicoRef] = useState<PorServico | null>(null);
  const [detalhe, setDetalhe] = useState<ServicoDetalhe | null>(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [detalheErro, setDetalheErro] = useState<string | null>(null);

  const fetchRef = useRef<() => void>(() => {});

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getTempoRegulacao(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        inicio,
        fim,
      });
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar o tempo de regulação.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, inicio, fim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar qualquer filtro
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const totais = data?.totais;
  const porServico = data?.por_servico || [];
  const meta = totais?.meta_dias ?? 30;
  const tempoMedioGlobalNaMeta = (totais?.tempo_medio_dias ?? 0) > 0 && (totais?.tempo_medio_dias ?? 0) <= meta;

  // Dados do grafico: servicos com regulações, por tempo medio (top 10)
  const chartData = useMemo(() => {
    return porServico
      .filter((s) => s.regulados > 0)
      .slice()
      .sort((a, b) => b.tempo_medio_dias - a.tempo_medio_dias)
      .slice(0, 10)
      .map((s) => ({
        nome: s.servico_nome.length > 24 ? s.servico_nome.slice(0, 23) + '…' : s.servico_nome,
        nomeCompleto: s.servico_nome,
        tempo: s.tempo_medio_dias,
        naMeta: s.na_meta,
      }));
  }, [porServico]);

  const abrirServico = useCallback((s: PorServico) => {
    if (!municipioAtivo?.id || s.servico_tipo_id == null) return;
    setServicoRef(s);
    setDetalhe(null);
    setDetalheErro(null);
    setDetalheLoading(true);
    saudeService
      .getTempoRegulacaoServico(municipioAtivo.id, {
        servicoTipoId: s.servico_tipo_id,
        unidade: unidadeSelecionada?.codunidade,
        inicio,
        fim,
      })
      .then((d: ServicoDetalhe) => setDetalhe(d))
      .catch((err: any) => setDetalheErro(err?.response?.data?.message || 'Erro ao carregar o detalhe do serviço.'))
      .finally(() => setDetalheLoading(false));
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, inicio, fim]);

  const fecharServico = useCallback(() => {
    setServicoRef(null);
    setDetalhe(null);
    setDetalheErro(null);
  }, []);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um município para visualizar o tempo de regulação.</Alert></Box>;
  }

  const detTotais = detalhe?.totais;
  const detNaMeta = (detTotais?.tempo_medio_dias ?? 0) > 0 && (detTotais?.tempo_medio_dias ?? 0) <= (detTotais?.meta_dias ?? meta);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentTurnedInIcon color="primary" /> Tempo de Regulação
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong></>
              : 'Todas as unidades do município'}
            {' '}• Tempo médio até a regulação das solicitações • Meta: {meta} dias
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado às ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Próxima atualização em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtros: periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small" type="date" label="Período (início)"
            value={inicio} onChange={(e) => setInicio(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <TextField
            size="small" type="date" label="Período (fim)"
            value={fim} onChange={(e) => setFim(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
            O período filtra as regulações concluídas (pela data da regulação). Use o seletor de Unidade de Saúde
            (acima) para restringir à unidade solicitante. Pendentes refletem a fila atual.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <KpiCard label="Tempo médio de regulação" value={formatarDias(totais?.tempo_medio_dias)} icon={<HourglassEmptyIcon />} color="#1976d2" loading={loading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="% dentro da meta" value={`${(totais?.na_meta_pct ?? 0).toLocaleString('pt-BR')}%`} icon={<PercentIcon />} color="#2e7d32" loading={loading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="Solicitações reguladas" value={(totais?.total_regulado ?? 0).toLocaleString('pt-BR')} icon={<AssignmentTurnedInIcon />} color="#7b1fa2" loading={loading} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard label="Pendentes na fila" value={(totais?.pendentes ?? 0).toLocaleString('pt-BR')} icon={<PendingActionsIcon />} color="#ef6c00" loading={loading} />
        </Grid>
      </Grid>

      {/* Chips: meta global */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, alignItems: 'center' }}>
        <Chip size="small" variant="outlined" icon={<FlagIcon sx={{ fontSize: 16 }} />} label={`Meta: até ${meta} dias`} />
        {(totais?.total_regulado ?? 0) > 0 && <ChipMeta naMeta={tempoMedioGlobalNaMeta} />}
      </Box>

      {/* Grafico: tempo medio por servico (com linha da meta) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Tempo médio de regulação por serviço</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Barras em vermelho indicam serviços acima da meta de {meta} dias.
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : chartData.length ? (
            <Box sx={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="nome" angle={-35} textAnchor="end" interval={0} height={90} tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} label={{ value: 'dias', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} />
                  <RTooltip
                    formatter={(v: any) => [formatarDias(Number(v)), 'Tempo médio']}
                    labelFormatter={(_l: any, p: any) => p?.[0]?.payload?.nomeCompleto || ''}
                  />
                  <ReferenceLine y={meta} stroke="#ef6c00" strokeDasharray="6 4"
                    label={{ value: `Meta ${meta}d`, position: 'right', fill: '#ef6c00', fontSize: 11 }} />
                  <Bar dataKey="tempo" radius={[4, 4, 0, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.naMeta ? '#2e7d32' : '#e53935'} />
                    ))}
                    <LabelList dataKey="tempo" position="top" formatter={(v: any) => Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} style={{ fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography color="text.secondary" variant="body2">Nenhuma regulação concluída no período selecionado.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Tabela por servico (clicavel) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Regulação por serviço</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Clique num serviço para ver o tempo médio de regulação, o status da meta e o detalhamento por profissional regulador.
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : porServico.length ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Serviço</TableCell>
                    <TableCell align="center">Reguladas</TableCell>
                    <TableCell align="center">Tempo médio</TableCell>
                    <TableCell align="center">Dentro da meta</TableCell>
                    <TableCell align="center">Pendentes</TableCell>
                    <TableCell align="center">Situação</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {porServico.map((s) => {
                    const clicavel = s.servico_tipo_id != null;
                    return (
                      <TableRow
                        key={`${s.servico_tipo_id}-${s.servico_nome}`}
                        hover={clicavel}
                        onClick={() => clicavel && abrirServico(s)}
                        sx={{ cursor: clicavel ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {s.servico_nome}
                        </TableCell>
                        <TableCell align="center">{s.regulados.toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{formatarDias(s.tempo_medio_dias)}</TableCell>
                        <TableCell align="center">{s.regulados > 0 ? `${s.dentro_meta}/${s.regulados}` : '—'}</TableCell>
                        <TableCell align="center">{s.pendentes.toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="center"><ChipMeta naMeta={s.na_meta} semDados={s.regulados === 0} /></TableCell>
                        <TableCell align="right">
                          {clicavel && <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" variant="body2">Nenhuma solicitação de regulação para os filtros selecionados.</Typography>
          )}
        </CardContent>
      </Card>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Tempo de regulação = intervalo entre a entrada na fila e a regulação da solicitação • Meta: ${meta} dias${unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ''}`} />

      {/* Detalhe do servico (POC 16.20 — tempo médio + meta + por profissional regulador) */}
      <Dialog
        open={!!servicoRef}
        onClose={fecharServico}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1, pr: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}>
          <AssignmentTurnedInIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>{servicoRef?.servico_nome}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              Tempo de regulação{unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ' • Todas as unidades'}
            </Typography>
          </Box>
          <IconButton onClick={fecharServico} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          {detalheLoading ? (
            <Box>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Box>
          ) : detalheErro ? (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={() => servicoRef && abrirServico(servicoRef)}>Tentar novamente</Button>
            }>{detalheErro}</Alert>
          ) : (
            <>
              {/* Status da meta */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" icon={<FlagIcon sx={{ fontSize: 16 }} />} label={`Meta: até ${detTotais?.meta_dias ?? meta} dias`} />
                {(detTotais?.total_regulado ?? 0) > 0
                  ? <ChipMeta naMeta={detNaMeta} />
                  : <ChipMeta naMeta={false} semDados />}
              </Box>

              {/* KPIs do serviço */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <KpiCard label="Tempo médio" value={formatarDias(detTotais?.tempo_medio_dias)} icon={<HourglassEmptyIcon />} color="#1976d2" loading={false} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard label="% na meta" value={`${(detTotais?.na_meta_pct ?? 0).toLocaleString('pt-BR')}%`} icon={<PercentIcon />} color="#2e7d32" loading={false} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard label="Reguladas" value={(detTotais?.total_regulado ?? 0).toLocaleString('pt-BR')} icon={<AssignmentTurnedInIcon />} color="#7b1fa2" loading={false} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KpiCard label="Pendentes" value={(detTotais?.pendentes ?? 0).toLocaleString('pt-BR')} icon={<PendingActionsIcon />} color="#ef6c00" loading={false} />
                </Grid>
              </Grid>

              {/* Por profissional regulador */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Por profissional regulador</Typography>
              {detalhe && detalhe.por_profissional.length > 0 ? (
                <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Profissional</TableCell>
                        <TableCell align="center">Reguladas</TableCell>
                        <TableCell align="center">Tempo médio</TableCell>
                        <TableCell align="center">Dentro da meta</TableCell>
                        <TableCell align="center">Situação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalhe.por_profissional.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <PersonIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                              <Typography variant="body2" fontWeight={600}>{p.regulador}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{p.regulados.toLocaleString('pt-BR')}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{formatarDias(p.tempo_medio_dias)}</TableCell>
                          <TableCell align="center">{p.regulados > 0 ? `${p.dentro_meta}/${p.regulados}` : '—'}</TableCell>
                          <TableCell align="center"><ChipMeta naMeta={p.na_meta} semDados={p.regulados === 0} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">Nenhuma regulação concluída para este serviço no período selecionado.</Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={fecharServico} variant="outlined" size="small">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SaudeTempoRegulacao;
